/**
 * The `appStateDM` store from the Qwik layout, as a React context.
 *
 * Holds the view flags the sidebar and the campaign pages share (drawer,
 * legend, status pills, approval mode, which panel is showing) plus the
 * resolved customer being worked on. Data itself stays in react-query.
 */

import { CANNOT_DELETE_MESSAGE, customerEditBlocked, lockNotice } from "@shared/campaign-rules.ts";
import { finalPdfPollMs } from "@shared/final-pdf.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  type Artwork,
  api,
  type Campaign,
  type CampaignZone,
  type Customer,
  type PriceTierRow,
  type Product,
  type SessionUser,
} from "../../lib/api.ts";
import { isCampaignPath, isStaffRole } from "../../lib/legacy.ts";
import { useShell } from "./Header.tsx";
import { Modal, useToast } from "./ui.tsx";

export type AppContext =
  | "map"
  | "uploadAddressCSV"
  | "manageArtwork"
  | "approveArtwork"
  | "confirmCampaign"
  | "myCampaigns"
  | "customers"
  | "orders";

export interface UiFlags {
  showAsideDrawer: boolean;
  debugMode: boolean;
  showIDs: boolean;
  showLegend: boolean;
  showStatusPills: boolean;
  showDeletedCampaigns: boolean;
  approveArtworkMode: boolean;
  appContext: AppContext;
  showAddressList: boolean;
  uploadExclusionFiles: boolean;
  showBusinessIcon: boolean;
  showResidentialIcon: boolean;
  hamburgerMenu: boolean;
  campaignActionsMenu: boolean;
  editCampaignMode: boolean;
  /** Name input only. Detail fields use editCampaignMode. */
  renameCampaignMode: boolean;
  /** Electoral controls from the sidebar, feeding the zone picker. */
  electoralLevel: string;
  electoralSubType: string;
}

/**
 * The three preferences worth keeping between visits: panel collapsed, status
 * labels shown, legend open.
 * ponytail: one key, no schema version; a stale shape just falls back to the
 * defaults. Upgrade path is a versioned key when a field changes meaning.
 */
const STORED_UI_KEY = "econnect.workspace-ui";
const STORED_UI_FIELDS = ["showAsideDrawer", "showStatusPills", "showLegend"] as const;
type StoredUi = Pick<UiFlags, (typeof STORED_UI_FIELDS)[number]>;

function readStoredUi(): Partial<StoredUi> {
  try {
    const raw = localStorage.getItem(STORED_UI_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const out: Partial<StoredUi> = {};
    for (const k of STORED_UI_FIELDS) if (typeof parsed[k] === "boolean") out[k] = parsed[k];
    return out;
  } catch {
    return {};
  }
}

function storeUi(ui: UiFlags) {
  try {
    const out: Partial<StoredUi> = {};
    for (const k of STORED_UI_FIELDS) out[k] = ui[k];
    localStorage.setItem(STORED_UI_KEY, JSON.stringify(out));
  } catch {
    // Storage full or blocked: the session still works, it just will not remember.
  }
}

/** Shared with the account rail so collapsing either drawer sticks for the next visit. */
export function asideDrawerOpen(): boolean {
  return readStoredUi().showAsideDrawer ?? true;
}

export function setAsideDrawerOpen(open: boolean) {
  try {
    const raw = localStorage.getItem(STORED_UI_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    const base =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    base.showAsideDrawer = open;
    localStorage.setItem(STORED_UI_KEY, JSON.stringify(base));
  } catch {
    // Storage full or blocked: the session still works, it just will not remember.
  }
}

/**
 * The last page outside a campaign, so Close can go back to wherever the
 * campaign was opened from (list, dashboard, catalog, cart).
 * ponytail: per-tab sessionStorage, one slot. A campaign opened in a fresh
 * tab has no entry and falls back to the campaign list.
 */
const CAMPAIGN_RETURN_KEY = "econnect.campaign-return";

export function rememberCampaignReturn(href: string) {
  if (isCampaignPath(href.split(/[?#]/)[0] ?? "")) return;
  try {
    sessionStorage.setItem(CAMPAIGN_RETURN_KEY, href);
  } catch {
    // Storage blocked: Close falls back to the campaign list.
  }
}

export function campaignReturnHref(): string | null {
  try {
    return sessionStorage.getItem(CAMPAIGN_RETURN_KEY);
  } catch {
    return null;
  }
}

const DEFAULT_UI: UiFlags = {
  showAsideDrawer: true,
  debugMode: false,
  showIDs: false,
  showLegend: false,
  showStatusPills: false,
  showDeletedCampaigns: false,
  approveArtworkMode: false,
  appContext: "myCampaigns",
  showAddressList: false,
  uploadExclusionFiles: false,
  showBusinessIcon: true,
  showResidentialIcon: true,
  hamburgerMenu: false,
  campaignActionsMenu: false,
  editCampaignMode: false,
  renameCampaignMode: false,
  electoralLevel: "state",
  electoralSubType: "districts",
};

export interface AppDm {
  user: SessionUser;
  customerId: string;
  customer: Customer | null;
  isCurrentAdmin: boolean;
  adminMode: boolean;
  ui: UiFlags;
  set: (patch: Partial<UiFlags>) => void;
  campaignId: string | null;
  campaign: Campaign | null;
  zones: CampaignZone[];
  campaigns: Campaign[];
  artworks: Artwork[];
  products: Product[];
  tiers: PriceTierRow[];
  loading: boolean;
  updateCampaign: (patch: Record<string, unknown>) => Promise<Campaign | undefined>;
  createCampaign: (productId?: string) => Promise<void>;
  duplicateCampaign: (id: string) => Promise<void>;
  deleteCampaign: (
    id: string,
    opts?: {
      skipConfirm?: boolean;
      skipRedirect?: boolean;
      waitForRefresh?: boolean;
      /** Runs synchronously, immediately before leaving the open campaign. */
      beforeRedirect?: () => void;
    },
  ) => Promise<void>;
  restoreCampaign: (id: string) => Promise<void>;
  /** Put a campaign in the cart. The server re-checks cartBlockers. */
  addToCart: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  /** A close-only explanation, used when a campaign rule blocks the click. */
  notice: (title: string, message: string) => void;
  pendingApprovalCount: number;
  rejectApprovalCount: number;
  /** Bumps when the open campaign title is clicked and the drawer is already open. */
  drawerPulse: number;
}

const Ctx = createContext<AppDm | null>(null);

export function useAppDm(): AppDm {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppDm outside AppDmProvider");
  return v;
}

interface ProviderProps {
  user: SessionUser;
  customerId: string;
  campaignId: string | null;
  children: ReactNode;
}

export function AppDmProvider({ user, customerId, campaignId, children }: ProviderProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setAdminMode, setCampaignHeader, setViewingCustomer, setShowCart, setLockNotice } =
    useShell();
  const isCurrentAdmin = isStaffRole(user.role);
  const adminMode = isCurrentAdmin;
  const [ui, setUi] = useState<UiFlags>(() => ({ ...DEFAULT_UI, ...readStoredUi() }));
  const [drawerPulse, setDrawerPulse] = useState(0);
  const rememberedCampaign = useRef<Campaign | null>(null);
  const campaignIdRef = useRef(campaignId);
  const customerIdRef = useRef(customerId);
  const uiRef = useRef(ui);
  campaignIdRef.current = campaignId;
  customerIdRef.current = customerId;
  uiRef.current = ui;
  const set = useCallback(
    (patch: Partial<UiFlags>) =>
      setUi((u) => {
        const next = { ...u, ...patch };
        storeUi(next);
        return next;
      }),
    [],
  );

  useEffect(() => {
    setAdminMode(adminMode);
    return () => setAdminMode(false);
  }, [adminMode, setAdminMode]);

  const customerQuery = useQuery({
    queryKey: ["customer", customerId, isCurrentAdmin],
    queryFn: async () => {
      if (isCurrentAdmin) {
        const { customer } = await api.get<{ customer: Customer }>(
          `/admin/customers/${customerId}`,
        );
        return customer;
      }
      const { profile } = await api.get<{ profile: Customer }>("/account/profile");
      return profile;
    },
  });

  const customer = customerQuery.data ?? null;
  useEffect(() => {
    const other = isCurrentAdmin && customerId !== user.id;
    setViewingCustomer(other ? { name: customer?.name ?? "this customer" } : null);
    return () => setViewingCustomer(null);
  }, [isCurrentAdmin, customerId, user.id, customer?.name, setViewingCustomer]);

  const campaignsQuery = useQuery({
    queryKey: ["campaigns", "app", isCurrentAdmin ? "all" : customerId],
    queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    refetchInterval: (query) => finalPdfPollMs(query.state.data?.campaigns),
  });

  const campaignQuery = useQuery({
    queryKey: ["campaign", campaignId],
    enabled: Boolean(campaignId),
    queryFn: () =>
      api.get<{ campaign: Campaign; zones: CampaignZone[] }>(`/campaigns/${campaignId}`),
  });

  const openCampaign = campaignId ? (campaignQuery.data?.campaign ?? null) : null;
  const campaignLock = openCampaign ? lockNotice(openCampaign, isCurrentAdmin) : null;
  useEffect(() => {
    setLockNotice(campaignLock);
    return () => setLockNotice(null);
  }, [campaignLock, setLockNotice]);

  const artworkQuery = useQuery({
    queryKey: ["artwork"],
    queryFn: () => api.get<{ artwork: Artwork[] }>("/artwork"),
  });

  const catalogQuery = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ products: Product[]; tiers: PriceTierRow[] }>("/catalog/products"),
  });

  const campaign = campaignQuery.data?.campaign ?? null;
  const products = catalogQuery.data?.products;

  const openCampaignHeader = useCallback(() => {
    const shown = rememberedCampaign.current;
    if (!shown) return;
    if (campaignIdRef.current === shown.id) {
      if (uiRef.current.showAsideDrawer) setDrawerPulse((n) => n + 1);
      else set({ showAsideDrawer: true });
      return;
    }
    navigate({
      to: "/app/$customerId/mycampaigns/$campaignId",
      params: { customerId: customerIdRef.current, campaignId: shown.id },
    });
  }, [navigate, set]);

  useEffect(() => {
    if (campaign) rememberedCampaign.current = campaign;
    const shown = campaign ?? rememberedCampaign.current;
    if (!shown) {
      setCampaignHeader(null);
      return;
    }
    setCampaignHeader({ name: shown.name, onOpen: openCampaignHeader });
  }, [campaign, openCampaignHeader, setCampaignHeader]);

  useEffect(() => {
    const shown = rememberedCampaign.current;
    const owner = shown?.customer_id ?? shown?.user_id;
    if (shown && owner !== customerId) {
      rememberedCampaign.current = null;
      setCampaignHeader(null);
    }
    return () => {
      rememberedCampaign.current = null;
      setCampaignHeader(null);
    };
  }, [customerId, setCampaignHeader]);

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
      queryClient.invalidateQueries({ queryKey: ["campaign"] }),
      queryClient.invalidateQueries({ queryKey: ["cart"] }),
    ]);
  }, [queryClient]);

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ campaign: Campaign }>(`/campaigns/${campaignId}`, patch),
    onSuccess: async (_data, patch) => {
      await invalidate();
      if ("artworkId" in patch || "designId" in patch) {
        await queryClient.invalidateQueries({ queryKey: ["artwork"] });
      }
      if (
        [
          "artworkId",
          "designId",
          "artworkPages",
          "salutation",
          "salutationCustom",
          "returnAddress",
          "mailSpeed",
          "mailType",
        ].some((key) => key in patch)
      ) {
        await queryClient.invalidateQueries({ queryKey: ["final-pdf"] });
      }
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  const updateCampaign = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!campaignId) return undefined;
      const res = await update.mutateAsync(patch);
      return res.campaign;
    },
    [campaignId, update],
  );

  const createCampaign = useCallback(
    async (productId?: string) => {
      const body: Record<string, unknown> = {
        name: "New Campaign",
        source: "map",
        mailType: "unaddressed",
        ...(isCurrentAdmin ? { customerId } : {}),
        ...(productId ? { productId } : {}),
      };
      const { campaign } = await api.post<{ campaign: Campaign }>("/campaigns", body);
      await invalidate();
      set({
        editCampaignMode: false,
        renameCampaignMode: false,
        campaignActionsMenu: false,
        appContext: "map",
        showAddressList: false,
      });
      navigate({
        to: "/app/$customerId/mycampaigns/$campaignId",
        params: { customerId, campaignId: campaign.id },
      });
    },
    [customerId, isCurrentAdmin, invalidate, navigate, set],
  );

  const duplicateCampaign = useCallback(
    async (id: string) => {
      const { campaign } = await api.post<{ campaign: Campaign }>(`/campaigns/${id}/duplicate`);
      await invalidate();
      showToast("Campaign duplicated", "success");
      set({ editCampaignMode: false, renameCampaignMode: false, campaignActionsMenu: false });
      navigate({
        to: "/app/$customerId/mycampaigns/$campaignId",
        params: { customerId, campaignId: campaign.id },
      });
    },
    [customerId, invalidate, navigate, set, showToast],
  );

  const pendingDelete = useRef<((ok: boolean) => void) | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [ruleNotice, setRuleNotice] = useState<{ title: string; message: string } | null>(null);
  const notice = useCallback((title: string, message: string) => {
    setRuleNotice({ title, message });
  }, []);
  const deleteCampaign = useCallback(
    async (
      id: string,
      opts?: {
        skipConfirm?: boolean;
        skipRedirect?: boolean;
        waitForRefresh?: boolean;
        beforeRedirect?: () => void;
      },
    ) => {
      const open = campaignQuery.data?.campaign;
      if (
        open?.id === id &&
        !isCurrentAdmin &&
        customerEditBlocked(open.status, open.order_status)
      ) {
        setRuleNotice({ title: "Delete campaign", message: CANNOT_DELETE_MESSAGE });
        return;
      }
      if (!opts?.skipConfirm) {
        const ok = await new Promise<boolean>((resolve) => {
          pendingDelete.current = resolve;
          setConfirmingDelete(true);
        });
        pendingDelete.current = null;
        setConfirmingDelete(false);
        if (!ok) return;
      }
      await api.delete(`/campaigns/${id}`);
      // Leaving discards without waiting on the list refresh. The menu delete
      // still waits, so the list does not briefly show a campaign already gone.
      if (opts?.waitForRefresh === false) void invalidate();
      else await invalidate();
      if (!opts?.skipRedirect && id === campaignId) {
        set({
          campaignActionsMenu: false,
          hamburgerMenu: false,
          appContext: "myCampaigns",
          showAddressList: false,
          approveArtworkMode: false,
          editCampaignMode: false,
          renameCampaignMode: false,
        });
        // No await after this: the leave guard must be down before navigate.
        opts?.beforeRedirect?.();
        navigate({
          to: "/app/$customerId/mycampaigns",
          params: { customerId },
          ignoreBlocker: true,
        });
      } else {
        set({ campaignActionsMenu: false });
      }
    },
    [campaignId, campaignQuery.data, customerId, invalidate, isCurrentAdmin, navigate, set],
  );

  const restoreCampaign = useCallback(
    async (id: string) => {
      await api.post(`/campaigns/${id}/restore`);
      await invalidate();
    },
    [invalidate],
  );

  const addToCart = useCallback(
    async (id: string) => {
      try {
        await api.patch(`/campaigns/${id}`, { inCart: true });
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not add to cart", "error");
        return;
      }
      await invalidate();
      showToast("Added to cart", "success");
    },
    [invalidate, showToast],
  );

  // A change to the open campaign while it sits in the cart changes what the
  // customer will pay, so say so. Zone edits, detail edits and renames all
  // move updated_at. Sonner, not the legacy one-slot toast, so the field's
  // own "updated" toast cannot overwrite it.
  // ponytail: updated_at is whole seconds, so two edits in one second toast once.
  const cartSeen = useRef<Pick<Campaign, "id" | "updated_at" | "in_cart_at"> | null>(null);
  useEffect(() => {
    if (!campaign) return;
    const prev = cartSeen.current;
    cartSeen.current = campaign;
    if (
      prev?.id !== campaign.id ||
      !prev.in_cart_at ||
      !campaign.in_cart_at ||
      campaign.updated_at <= prev.updated_at
    )
      return;
    toast.custom(
      (t) => (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-lg">
          <div className="min-w-0 flex-1">
            <p className="font-semibold">The product in your cart has been updated</p>
            <p className="mt-0.5 text-amber-800">
              {campaign.name} changed. Check the price before you pay.
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 font-semibold text-amber-900 underline hover:text-amber-700"
            onClick={() => {
              toast.dismiss(t);
              setShowCart(true);
            }}
          >
            View Cart
          </button>
        </div>
      ),
      { id: `cart-updated-${campaign.id}`, duration: 6000 },
    );
  }, [campaign, setShowCart]);

  const campaigns = campaignsQuery.data?.campaigns ?? [];
  const forCustomer = campaigns.filter((c) => (c.customer_id ?? c.user_id) === customerId);
  const pendingApprovalCount = campaigns.filter(
    (c) => c.approval_status === "approved_by_creator" && c.status !== "deleted",
  ).length;
  const rejectApprovalCount = forCustomer.filter(
    (c) => c.approval_status === "rejected" && c.status !== "deleted",
  ).length;

  const value = useMemo<AppDm>(
    () => ({
      user,
      customerId,
      customer: customerQuery.data ?? null,
      isCurrentAdmin,
      adminMode,
      ui,
      set,
      campaignId,
      campaign,
      zones: campaignQuery.data?.zones ?? [],
      campaigns,
      artworks: artworkQuery.data?.artwork ?? [],
      products: products ?? [],
      tiers: catalogQuery.data?.tiers ?? [],
      loading: campaignsQuery.isLoading || (Boolean(campaignId) && campaignQuery.isLoading),
      updateCampaign,
      createCampaign,
      duplicateCampaign,
      deleteCampaign,
      restoreCampaign,
      addToCart,
      refresh: invalidate,
      notice,
      pendingApprovalCount,
      rejectApprovalCount,
      drawerPulse,
    }),
    [
      user,
      customerId,
      customerQuery.data,
      isCurrentAdmin,
      adminMode,
      ui,
      set,
      campaignId,
      campaign,
      campaignQuery.data,
      campaignQuery.isLoading,
      campaigns,
      campaignsQuery.isLoading,
      artworkQuery.data,
      products,
      catalogQuery.data,
      updateCampaign,
      createCampaign,
      duplicateCampaign,
      deleteCampaign,
      restoreCampaign,
      addToCart,
      invalidate,
      notice,
      pendingApprovalCount,
      rejectApprovalCount,
      drawerPulse,
    ],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <Modal
        title="Delete this campaign?"
        open={confirmingDelete}
        submitText="Delete"
        compact
        danger
        onSubmit={() => pendingDelete.current?.(true)}
        onCancel={() => pendingDelete.current?.(false)}
      >
        <p className="text-xs text-gray-400">
          Deleted campaigns can be retrieved from the Campaigns page!
        </p>
      </Modal>
      <Modal
        title={ruleNotice?.title ?? ""}
        open={Boolean(ruleNotice)}
        compact
        onClose={() => setRuleNotice(null)}
      >
        <p>{ruleNotice?.message}</p>
      </Modal>
    </Ctx.Provider>
  );
}
